(() => {

  'use strict';


  /* =========================================================
     PEERNEST — FULL COLLEGE CMS DATA EDITOR
     Frontend only.
     Uses existing PeerNestDB.request().
     ========================================================= */


  const escapeHTML =
    value =>
      String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');


  const clean =
    value => {

      const result =
        String(
          value ?? ''
        ).trim();

      return result || null;

    };


  const numberOrNull =
    value => {

      const text =
        String(
          value ?? ''
        ).trim();

      if (!text) {
        return null;
      }

      const number =
        Number(text);

      return Number.isFinite(number)
        ? number
        : null;

    };


  const csvToArray =
    value =>
      String(
        value ?? ''
      )
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);


  const arrayToCSV =
    value =>
      Array.isArray(value)
        ? value.join(', ')
        : String(
            value ?? ''
          );


  function notify(
    message,
    error =
      false
  ) {

    document
      .querySelectorAll(
        '.pncms-data-toast'
      )
      .forEach(
        node => node.remove()
      );


    const node =
      document.createElement(
        'div'
      );

    node.className =
      `pncms-data-toast ${
        error
          ? 'is-error'
          : ''
      }`;

    node.textContent =
      message;

    document.body.appendChild(
      node
    );


    window.setTimeout(
      () => node.remove(),
      3000
    );

  }


  async function waitForDB() {

    for (
      let attempt = 0;
      attempt < 60;
      attempt += 1
    ) {

      if (
        typeof window
          .PeerNestDB
          ?.request ===
        'function'
      ) {

        return window.PeerNestDB;

      }

      await new Promise(
        resolve =>
          window.setTimeout(
            resolve,
            100
          )
      );

    }


    throw new Error(
      'PeerNest database client is not available.'
    );

  }


  function fieldHTML(
    field,
    value
  ) {

    const wide =
      field.wide
        ? 'pncms-data-field--wide'
        : '';

    const label =
      `${field.label}${
        field.required
          ? ' *'
          : ''
      }`;

    let actual =
      value;


    if (
      actual === undefined ||
      actual === null
    ) {

      actual =
        field.default ?? '';

    }


    if (
      field.kind ===
      'checkbox'
    ) {

      return `
        <label class="pncms-data-toggle ${wide}">

          <div>
            <strong>
              ${escapeHTML(field.label)}
            </strong>

            ${
              field.help
                ? `
                    <small>
                      ${escapeHTML(field.help)}
                    </small>
                  `
                : ''
            }
          </div>

          <input
            type="checkbox"
            name="${escapeHTML(field.name)}"
            ${actual ? 'checked' : ''}
          >

        </label>
      `;

    }


    if (
      field.kind ===
      'textarea'
    ) {

      return `
        <label class="pncms-data-field ${wide}">

          <span>${escapeHTML(label)}</span>

          <textarea
            name="${escapeHTML(field.name)}"
            placeholder="${escapeHTML(
              field.placeholder ||
              ''
            )}"
            ${field.required ? 'required' : ''}
          >${escapeHTML(actual)}</textarea>

        </label>
      `;

    }


    const type =
      field.kind ===
      'number'
        ? 'number'
        : field.kind ===
          'date'
            ? 'date'
            : field.kind ===
              'url'
                ? 'url'
                : 'text';


    return `
      <label class="pncms-data-field ${wide}">

        <span>${escapeHTML(label)}</span>

        <input
          type="${type}"
          name="${escapeHTML(field.name)}"
          value="${escapeHTML(
            field.kind ===
            'csv'
              ? arrayToCSV(actual)
              : actual
          )}"
          placeholder="${escapeHTML(
            field.placeholder ||
            ''
          )}"
          ${
            field.step
              ? `step="${escapeHTML(field.step)}"`
              : ''
          }
          ${field.required ? 'required' : ''}
        >

      </label>
    `;

  }


  function fieldsHTML(
    fields,
    row = {}
  ) {

    return fields
      .map(
        field =>
          fieldHTML(
            field,
            row[field.name]
          )
      )
      .join('');

  }


  const configs = {

    admissions: {

      table:
        'college_admissions',

      title:
        'Admissions',

      description:
        'Eligibility, admission process, application dates and counselling information.',

      fields:[
        {
          name:'academic_year',
          label:'Academic year',
          placeholder:'2026–27'
        },
        {
          name:'eligibility',
          label:'Eligibility',
          kind:'textarea',
          wide:true,
          placeholder:'Eligibility requirements...'
        },
        {
          name:'admission_process',
          label:'Admission process',
          kind:'textarea',
          wide:true,
          placeholder:'Explain the admission process...'
        },
        {
          name:'application_start',
          label:'Application start',
          kind:'date'
        },
        {
          name:'application_deadline',
          label:'Application deadline',
          kind:'date'
        },
        {
          name:'counselling_details',
          label:'Counselling details',
          kind:'textarea',
          wide:true
        },
        {
          name:'notes',
          label:'Notes',
          kind:'textarea',
          wide:true
        }
      ]

    },


    fees: {

      table:
        'college_fees',

      title:
        'Fees',

      description:
        'Add tuition, hostel, application or other fee records.',

      fields:[
        {
          name:'academic_year',
          label:'Academic year',
          placeholder:'2026–27'
        },
        {
          name:'fee_type',
          label:'Fee type',
          required:true,
          placeholder:'Tuition'
        },
        {
          name:'amount',
          label:'Amount',
          kind:'number',
          step:'0.01',
          placeholder:'250000'
        },
        {
          name:'currency',
          label:'Currency',
          required:true,
          default:'INR'
        },
        {
          name:'billing_period',
          label:'Billing period',
          placeholder:'Per year'
        },
        {
          name:'notes',
          label:'Notes',
          kind:'textarea',
          wide:true
        }
      ]

    },


    placements: {

      table:
        'college_placements',

      title:
        'Placements',

      description:
        'Manage placement rate, packages, recruiters and placement-year data.',

      fields:[
        {
          name:'placement_year',
          label:'Placement year',
          kind:'number',
          placeholder:'2026'
        },
        {
          name:'placement_rate',
          label:'Placement rate',
          kind:'number',
          step:'0.01',
          placeholder:'92'
        },
        {
          name:'average_package',
          label:'Average package',
          kind:'number',
          step:'0.01',
          placeholder:'1200000'
        },
        {
          name:'median_package',
          label:'Median package',
          kind:'number',
          step:'0.01'
        },
        {
          name:'highest_package',
          label:'Highest package',
          kind:'number',
          step:'0.01',
          placeholder:'6500000'
        },
        {
          name:'currency',
          label:'Currency',
          required:true,
          default:'INR'
        },
        {
          name:'top_recruiters',
          label:'Top recruiters',
          kind:'csv',
          wide:true,
          placeholder:'Google, Microsoft, Amazon'
        },
        {
          name:'notes',
          label:'Notes',
          kind:'textarea',
          wide:true
        }
      ]

    },


    insights: {

      table:
        'college_student_insights',

      title:
        'Student Insights',

      description:
        'Concise student-facing insights shown on the college detail page.',

      fields:[
        {
          name:'insight_type',
          label:'Insight type',
          placeholder:'Campus culture'
        },
        {
          name:'title',
          label:'Title',
          placeholder:'What I wish I knew'
        },
        {
          name:'body',
          label:'Insight',
          kind:'textarea',
          wide:true,
          required:true
        },
        {
          name:'is_verified',
          label:'Verified',
          kind:'checkbox',
          default:true,
          help:'Mark this insight as verified.'
        },
        {
          name:'is_published',
          label:'Published',
          kind:'checkbox',
          default:true,
          help:'Show this insight to students.'
        }
      ]

    },


    faqs: {

      table:
        'college_faqs',

      title:
        'FAQs',

      description:
        'Questions and answers shown on the college detail page.',

      fields:[
        {
          name:'question',
          label:'Question',
          required:true,
          wide:true
        },
        {
          name:'answer',
          label:'Answer',
          kind:'textarea',
          required:true,
          wide:true
        },
        {
          name:'sort_order',
          label:'Sort order',
          kind:'number',
          default:0
        },
        {
          name:'is_published',
          label:'Published',
          kind:'checkbox',
          default:true,
          help:'Show this FAQ publicly.'
        }
      ]

    }

  };


  function rowTitle(
    type,
    row,
    index
  ) {

    if (type === 'admissions') {
      return (
        row.academic_year ||
        `Admission record ${index + 1}`
      );
    }

    if (type === 'fees') {
      return (
        row.fee_type ||
        `Fee record ${index + 1}`
      );
    }

    if (type === 'placements') {
      return (
        row.placement_year
          ? `Placement ${row.placement_year}`
          : `Placement record ${index + 1}`
      );
    }

    if (type === 'insights') {
      return (
        row.title ||
        `Insight ${index + 1}`
      );
    }

    if (type === 'faqs') {
      return (
        row.question ||
        `FAQ ${index + 1}`
      );
    }

    return `Record ${index + 1}`;

  }


  function simpleManagerHTML(
    type,
    rows,
    collegeId
  ) {

    const config =
      configs[type];


    if (!config) {
      return '';
    }


    const existing =
      rows
        .map(
          (row,index) => `
            <form
              class="pncms-data-card"
              data-pncms-related-form="${escapeHTML(type)}"
              data-pncms-row-id="${escapeHTML(row.id)}"
              data-pncms-college-id="${escapeHTML(collegeId)}"
            >

              <div class="pncms-data-card__head">

                <div>
                  <strong>
                    ${escapeHTML(
                      rowTitle(
                        type,
                        row,
                        index
                      )
                    )}
                  </strong>

                  <span>
                    Existing record
                  </span>
                </div>

                <button
                  type="button"
                  class="pncms-data-danger"
                  data-pncms-data-delete
                  data-pncms-table="${escapeHTML(config.table)}"
                  data-pncms-id="${escapeHTML(row.id)}"
                  data-pncms-college="${escapeHTML(collegeId)}"
                >
                  Delete
                </button>

              </div>


              <div class="pncms-data-grid">
                ${fieldsHTML(
                  config.fields,
                  row
                )}
              </div>


              <div class="pncms-data-savebar">

                <button
                  type="button"
                  class="pncms-data-save"
                  data-pncms-data-save
                >
                  Save changes
                </button>

              </div>

            </form>
          `
        )
        .join('');


    return `
      <div class="pncms-data-manager">

        <header class="pncms-data-manager__head">

          <div>
            <h4>${escapeHTML(config.title)}</h4>
            <p>${escapeHTML(config.description)}</p>
          </div>

          <span class="pncms-data-count">
            ${rows.length}
          </span>

        </header>


        <details class="pncms-data-new">

          <summary>
            + Add ${escapeHTML(
              config.title === 'FAQs'
                ? 'FAQ'
                : config.title.replace(/s$/, '')
            )}
          </summary>

          <form
            class="pncms-data-card pncms-data-card--new"
            data-pncms-related-form="${escapeHTML(type)}"
            data-pncms-college-id="${escapeHTML(collegeId)}"
          >

            <div class="pncms-data-grid">
              ${fieldsHTML(
                config.fields,
                {}
              )}
            </div>

            <div class="pncms-data-savebar">

              <button
                type="button"
                class="pncms-data-save"
                data-pncms-data-save
              >
                Add record
              </button>

            </div>

          </form>

        </details>


        <div class="pncms-data-list">

          ${
            existing ||
            `
              <div class="pncms-data-empty">
                No records added yet.
              </div>
            `
          }

        </div>

      </div>
    `;

  }


  function courseOptions(
    courses,
    selected
  ) {

    return `
      <option value="">
        Choose a program
      </option>

      ${
        courses
          .map(
            course => `
              <option
                value="${escapeHTML(course.id)}"
                ${
                  String(course.id) ===
                  String(selected || '')
                    ? 'selected'
                    : ''
                }
              >
                ${escapeHTML(course.name)}
                ${
                  course.level
                    ? ` · ${escapeHTML(course.level)}`
                    : ''
                }
              </option>
            `
          )
          .join('')
      }
    `;

  }


  function programForm(
    courses,
    link,
    collegeId,
    isNew =
      false
  ) {

    return `
      <form
        class="pncms-data-card ${
          isNew
            ? 'pncms-data-card--new'
            : ''
        }"
        data-pncms-related-form="course"
        data-pncms-college-id="${escapeHTML(collegeId)}"
        ${
          link?.id
            ? `data-pncms-row-id="${escapeHTML(link.id)}"`
            : ''
        }
      >

        ${
          !isNew
            ? `
                <div class="pncms-data-card__head">

                  <div>
                    <strong>
                      Program
                    </strong>

                    <span>
                      College program relationship
                    </span>
                  </div>

                  <button
                    type="button"
                    class="pncms-data-danger"
                    data-pncms-data-delete
                    data-pncms-table="college_courses"
                    data-pncms-id="${escapeHTML(link.id)}"
                    data-pncms-college="${escapeHTML(collegeId)}"
                  >
                    Remove
                  </button>

                </div>
              `
            : ''
        }


        <div class="pncms-data-grid">

          <label class="pncms-data-field pncms-data-field--wide">
            <span>Program *</span>

            <select
              name="course_id"
              required
            >
              ${courseOptions(
                courses,
                link?.course_id
              )}
            </select>
          </label>


          ${fieldHTML(
            {
              name:'department',
              label:'Department'
            },
            link?.department
          )}


          ${fieldHTML(
            {
              name:'specialization',
              label:'Specialization'
            },
            link?.specialization
          )}


          ${fieldHTML(
            {
              name:'duration_years',
              label:'Duration (years)',
              kind:'number',
              step:'0.5'
            },
            link?.duration_years
          )}


          ${fieldHTML(
            {
              name:'seats',
              label:'Seats',
              kind:'number'
            },
            link?.seats
          )}


          ${fieldHTML(
            {
              name:'is_active',
              label:'Active program',
              kind:'checkbox',
              default:true
            },
            link?.is_active ??
            true
          )}

        </div>


        <div class="pncms-data-savebar">

          <button
            type="button"
            class="pncms-data-save"
            data-pncms-data-save
          >
            ${
              isNew
                ? 'Add program'
                : 'Save program'
            }
          </button>

        </div>

      </form>
    `;

  }


  function programsHTML(
    courses,
    links,
    collegeId
  ) {

    return `
      <div class="pncms-data-manager">

        <header class="pncms-data-manager__head">

          <div>
            <h4>Programs & Academics</h4>

            <p>
              Link programs to this college and manage
              department, specialization, duration and seats.
            </p>
          </div>

          <span class="pncms-data-count">
            ${links.length}
          </span>

        </header>


        <details class="pncms-data-new">

          <summary>
            + Add Program
          </summary>

          ${
            programForm(
              courses,
              {},
              collegeId,
              true
            )
          }

        </details>


        <div class="pncms-data-list">

          ${
            links.length
              ? links
                  .map(
                    link =>
                      programForm(
                        courses,
                        link,
                        collegeId
                      )
                  )
                  .join('')
              : `
                  <div class="pncms-data-empty">
                    No programs linked yet.
                  </div>
                `
          }

        </div>

      </div>
    `;

  }


  function examOptions(
    exams,
    selected
  ) {

    return `
      <option value="">
        Choose an entrance exam
      </option>

      ${
        exams
          .map(
            exam => `
              <option
                value="${escapeHTML(exam.id)}"
                ${
                  String(exam.id) ===
                  String(selected || '')
                    ? 'selected'
                    : ''
                }
              >
                ${escapeHTML(exam.name)}
              </option>
            `
          )
          .join('')
      }
    `;

  }


  function collegeCourseOptions(
    links,
    courses,
    selected
  ) {

    const courseMap =
      new Map(
        courses.map(
          course => [
            String(course.id),
            course
          ]
        )
      );


    return `
      <option value="">
        Applies to whole college
      </option>

      ${
        links
          .map(
            link => {

              const course =
                courseMap.get(
                  String(link.course_id)
                );

              return `
                <option
                  value="${escapeHTML(link.id)}"
                  ${
                    String(link.id) ===
                    String(selected || '')
                      ? 'selected'
                      : ''
                  }
                >
                  ${escapeHTML(
                    course?.name ||
                    'Program'
                  )}
                  ${
                    link.specialization
                      ? ` · ${escapeHTML(link.specialization)}`
                      : ''
                  }
                </option>
              `;

            }
          )
          .join('')
      }
    `;

  }


  function examForm(
    exams,
    courseLinks,
    courses,
    row,
    collegeId,
    isNew =
      false
  ) {

    return `
      <form
        class="pncms-data-card ${
          isNew
            ? 'pncms-data-card--new'
            : ''
        }"
        data-pncms-related-form="exam"
        data-pncms-college-id="${escapeHTML(collegeId)}"
        ${
          row?.id
            ? `data-pncms-row-id="${escapeHTML(row.id)}"`
            : ''
        }
      >

        ${
          !isNew
            ? `
                <div class="pncms-data-card__head">

                  <div>
                    <strong>
                      Entrance exam
                    </strong>

                    <span>
                      College exam requirement
                    </span>
                  </div>

                  <button
                    type="button"
                    class="pncms-data-danger"
                    data-pncms-data-delete
                    data-pncms-table="college_entrance_exams"
                    data-pncms-id="${escapeHTML(row.id)}"
                    data-pncms-college="${escapeHTML(collegeId)}"
                  >
                    Remove
                  </button>

                </div>
              `
            : ''
        }


        <div class="pncms-data-grid">

          <label class="pncms-data-field">
            <span>Exam *</span>

            <select
              name="exam_id"
              required
            >
              ${examOptions(
                exams,
                row?.exam_id
              )}
            </select>
          </label>


          <label class="pncms-data-field">
            <span>Program</span>

            <select name="college_course_id">
              ${collegeCourseOptions(
                courseLinks,
                courses,
                row?.college_course_id
              )}
            </select>
          </label>


          ${fieldHTML(
            {
              name:'is_required',
              label:'Required exam',
              kind:'checkbox',
              default:true
            },
            row?.is_required ??
            true
          )}


          ${fieldHTML(
            {
              name:'notes',
              label:'Notes',
              kind:'textarea',
              wide:true
            },
            row?.notes
          )}

        </div>


        <div class="pncms-data-savebar">

          <button
            type="button"
            class="pncms-data-save"
            data-pncms-data-save
          >
            ${
              isNew
                ? 'Add exam'
                : 'Save exam'
            }
          </button>

        </div>

      </form>
    `;

  }


  function examsHTML(
    exams,
    rows,
    courseLinks,
    courses,
    collegeId
  ) {

    return `
      <div class="pncms-data-manager">

        <header class="pncms-data-manager__head">

          <div>
            <h4>Entrance Exams</h4>

            <p>
              Connect accepted exams and optionally attach
              them to a specific college program.
            </p>
          </div>

          <span class="pncms-data-count">
            ${rows.length}
          </span>

        </header>


        <details class="pncms-data-new">

          <summary>
            + Add Entrance Exam
          </summary>

          ${
            examForm(
              exams,
              courseLinks,
              courses,
              {},
              collegeId,
              true
            )
          }

        </details>


        <div class="pncms-data-list">

          ${
            rows.length
              ? rows
                  .map(
                    row =>
                      examForm(
                        exams,
                        courseLinks,
                        courses,
                        row,
                        collegeId
                      )
                  )
                  .join('')
              : `
                  <div class="pncms-data-empty">
                    No entrance exams linked yet.
                  </div>
                `
          }

        </div>

      </div>
    `;

  }


  const comparisonCampusFields = [
    {
      name:'campus_life_summary',
      label:'Campus life',
      kind:'textarea',
      wide:true,
      placeholder:'What does day-to-day campus life feel like?'
    },
    {
      name:'hostel_summary',
      label:'Hostel & accommodation',
      kind:'textarea',
      wide:true
    },
    {
      name:'clubs_summary',
      label:'Clubs & activities',
      kind:'textarea',
      wide:true
    },
    {
      name:'location_summary',
      label:'Location',
      kind:'textarea',
      wide:true
    },
    {
      name:'student_culture_summary',
      label:'Student culture',
      kind:'textarea',
      wide:true
    }
  ];


  const comparisonCoreFields = [
    {
      name:'academics_summary',
      label:'Academics',
      kind:'textarea',
      wide:true
    },
    {
      name:'fees_summary',
      label:'Fees',
      kind:'textarea',
      wide:true
    },
    {
      name:'entrance_exams_summary',
      label:'Entrance exams',
      kind:'textarea',
      wide:true
    },
    {
      name:'placements_summary',
      label:'Placements',
      kind:'textarea',
      wide:true
    },
    {
      name:'student_insights_summary',
      label:'Student insights',
      kind:'textarea',
      wide:true
    }
  ];


  function comparisonHTML(
    row,
    collegeId,
    mode
  ) {

    const fields =
      mode ===
      'campus'
        ? comparisonCampusFields
        : comparisonCoreFields;


    return `
      <div class="pncms-data-manager">

        <header class="pncms-data-manager__head">

          <div>

            <h4>
              ${
                mode ===
                'campus'
                  ? 'Campus, Hostel & Culture'
                  : 'Compare Data'
              }
            </h4>

            <p>
              ${
                mode ===
                'campus'
                  ? 'These summaries appear on the college page and also power Compare.'
                  : 'These summaries power the remaining fields on PeerNest Compare.'
              }
            </p>

          </div>

        </header>


        <form
          class="pncms-data-card"
          data-pncms-related-form="comparison"
          data-pncms-college-id="${escapeHTML(collegeId)}"
          ${
            row?.id
              ? `data-pncms-row-id="${escapeHTML(row.id)}"`
              : ''
          }
        >

          <div class="pncms-data-grid">

            ${fieldsHTML(
              fields,
              row ||
              {}
            )}

          </div>


          <div class="pncms-data-savebar">

            <button
              type="button"
              class="pncms-data-save"
              data-pncms-data-save
            >
              Save ${
                mode ===
                'campus'
                  ? 'campus data'
                  : 'compare data'
              }
            </button>

          </div>

        </form>

      </div>
    `;

  }


  function loadingHTML() {

    return `
      <div class="pncms-related-loading">
        <span></span>
        Loading college data…
      </div>
    `;

  }


  function saveFirstHTML() {

    return `
      <div class="pncms-data-empty pncms-data-empty--large">

        <strong>
          Save the college overview first
        </strong>

        <span>
          Related college data needs a College ID before it can be added.
        </span>

      </div>
    `;

  }


  function errorHTML(
    message
  ) {

    return `
      <div class="pncms-data-empty pncms-data-empty--large">

        <strong>
          Could not load this section
        </strong>

        <span>
          ${escapeHTML(message)}
        </span>

      </div>
    `;

  }


  function insertExtraOverview(
    form
  ) {

    const overview =
      form.querySelector(
        '[data-pncms-panel="overview"]'
      );


    if (
      !overview ||
      overview.querySelector(
        '[data-pncms-extra-overview]'
      )
    ) {

      return;

    }


    const section =
      document.createElement(
        'div'
      );

    section.className =
      'pncms-editor__section';

    section.dataset
      .pncmsExtraOverview =
      'true';


    section.innerHTML =
      `
        <div class="pncms-editor__heading">

          <strong>
            Discovery & verification
          </strong>

          <span>
            Additional college information used for credibility and search.
          </span>

        </div>


        <div class="pncms-editor__grid">

          <label class="pncms-field">

            <span>
              Accreditation
            </span>

            <input
              type="text"
              name="accreditation"
              placeholder="NAAC A++"
            >

          </label>


          <label class="pncms-field">

            <span>
              Official website
            </span>

            <input
              type="url"
              name="website_url"
              placeholder="https://..."
            >

          </label>


          <label class="pncms-field pncms-field--wide">

            <span>
              Search keywords
            </span>

            <input
              type="text"
              name="search_keywords"
              placeholder="engineering, bengaluru, private university"
            >

          </label>

        </div>
      `;


    overview.appendChild(
      section
    );

  }


  const tabs = [
    ['academics', 'Academics'],
    ['exams', 'Exams'],
    ['admissions', 'Admissions'],
    ['fees', 'Fees'],
    ['placements', 'Placements'],
    ['campus', 'Campus & Hostel'],
    ['insights', 'Insights'],
    ['faqs', 'FAQs'],
    ['compare', 'Compare Data']
  ];


  function insertTabsAndPanels(
    form
  ) {

    const tabBar =
      form.querySelector(
        '.pncms-editor__tabs'
      );


    const body =
      form.querySelector(
        '.pncms-editor__body'
      );


    if (
      !tabBar ||
      !body
    ) {

      return;

    }


    const mediaTab =
      tabBar.querySelector(
        '[data-pncms-tab="media"]'
      );


    tabs.forEach(
      ([key,label]) => {

        if (
          tabBar.querySelector(
            `[data-pncms-tab="${key}"]`
          )
        ) {

          return;

        }


        const button =
          document.createElement(
            'button'
          );

        button.type =
          'button';

        button.className =
          'pncms-editor__tab';

        button.dataset
          .pncmsTab =
          key;

        button.textContent =
          label;


        if (mediaTab) {

          tabBar.insertBefore(
            button,
            mediaTab
          );

        }
        else {

          tabBar.appendChild(
            button
          );

        }

      }
    );


    const mediaPanel =
      body.querySelector(
        '[data-pncms-panel="media"]'
      );


    tabs.forEach(
      ([key]) => {

        if (
          body.querySelector(
            `[data-pncms-panel="${key}"]`
          )
        ) {

          return;

        }


        const panel =
          document.createElement(
            'div'
          );

        panel.className =
          'pncms-editor__panel';

        panel.dataset
          .pncmsPanel =
          key;

        panel.innerHTML =
          `<div data-pncms-related="${key}">${loadingHTML()}</div>`;


        if (mediaPanel) {

          body.insertBefore(
            panel,
            mediaPanel
          );

        }
        else {

          body.appendChild(
            panel
          );

        }

      }
    );

  }


  function setRelatedHTML(
    form,
    key,
    html
  ) {

    const container =
      form.querySelector(
        `[data-pncms-related="${key}"]`
      );


    if (container) {

      container.innerHTML =
        html;

    }

  }


  async function loadCollegeData(
    form
  ) {

    const collegeId =
      form.dataset
        .pncmsId;


    if (!collegeId) {

      tabs.forEach(
        ([key]) =>
          setRelatedHTML(
            form,
            key,
            saveFirstHTML()
          )
      );

      return;

    }


    tabs.forEach(
      ([key]) =>
        setRelatedHTML(
          form,
          key,
          loadingHTML()
        )
    );


    try {

      const DB =
        await waitForDB();


      const id =
        encodeURIComponent(
          collegeId
        );


      const [
        collegeRows,
        courses,
        courseLinks,
        exams,
        examLinks,
        admissions,
        fees,
        placements,
        comparisonRows,
        insights,
        faqs
      ] =
        await Promise.all([

          DB.request(
            'colleges',
            `select=accreditation,website_url,search_keywords&id=eq.${id}&limit=1`
          ),

          DB.request(
            'courses',
            'select=*&order=name.asc'
          ),

          DB.request(
            'college_courses',
            `select=*&college_id=eq.${id}&order=created_at.asc`
          ),

          DB.request(
            'entrance_exams',
            'select=*&order=name.asc'
          ),

          DB.request(
            'college_entrance_exams',
            `select=*&college_id=eq.${id}&order=created_at.asc`
          ),

          DB.request(
            'college_admissions',
            `select=*&college_id=eq.${id}&order=created_at.asc`
          ),

          DB.request(
            'college_fees',
            `select=*&college_id=eq.${id}&order=created_at.asc`
          ),

          DB.request(
            'college_placements',
            `select=*&college_id=eq.${id}&order=placement_year.desc`
          ),

          DB.request(
            'college_comparison',
            `select=*&college_id=eq.${id}&limit=1`
          ),

          DB.request(
            'college_student_insights',
            `select=*&college_id=eq.${id}&order=created_at.asc`
          ),

          DB.request(
            'college_faqs',
            `select=*&college_id=eq.${id}&order=sort_order.asc`
          )

        ]);


      const college =
        Array.isArray(
          collegeRows
        )
          ? collegeRows[0]
          : null;


      const accreditation =
        form.querySelector(
          '[name="accreditation"]'
        );

      const website =
        form.querySelector(
          '[name="website_url"]'
        );

      const keywords =
        form.querySelector(
          '[name="search_keywords"]'
        );


      if (accreditation) {

        accreditation.value =
          college?.accreditation ||
          '';

      }


      if (website) {

        website.value =
          college?.website_url ||
          '';

      }


      if (keywords) {

        keywords.value =
          arrayToCSV(
            college?.search_keywords
          );

      }


      const comparison =
        Array.isArray(
          comparisonRows
        )
          ? comparisonRows[0]
          : null;


      setRelatedHTML(
        form,
        'academics',
        programsHTML(
          courses || [],
          courseLinks || [],
          collegeId
        )
      );


      setRelatedHTML(
        form,
        'exams',
        examsHTML(
          exams || [],
          examLinks || [],
          courseLinks || [],
          courses || [],
          collegeId
        )
      );


      setRelatedHTML(
        form,
        'admissions',
        simpleManagerHTML(
          'admissions',
          admissions || [],
          collegeId
        )
      );


      setRelatedHTML(
        form,
        'fees',
        simpleManagerHTML(
          'fees',
          fees || [],
          collegeId
        )
      );


      setRelatedHTML(
        form,
        'placements',
        simpleManagerHTML(
          'placements',
          placements || [],
          collegeId
        )
      );


      setRelatedHTML(
        form,
        'campus',
        comparisonHTML(
          comparison,
          collegeId,
          'campus'
        )
      );


      setRelatedHTML(
        form,
        'insights',
        simpleManagerHTML(
          'insights',
          insights || [],
          collegeId
        )
      );


      setRelatedHTML(
        form,
        'faqs',
        simpleManagerHTML(
          'faqs',
          faqs || [],
          collegeId
        )
      );


      setRelatedHTML(
        form,
        'compare',
        comparisonHTML(
          comparison,
          collegeId,
          'compare'
        )
      );

    }


    catch (error) {

      console.error(
        '[PeerNest College CMS] load:',
        error
      );


      tabs.forEach(
        ([key]) =>
          setRelatedHTML(
            form,
            key,
            errorHTML(
              error?.message ||
              'Unknown error'
            )
          )
      );


      notify(
        error?.message ||
        'Could not load college data.',
        true
      );

    }

  }


  function readConfigPayload(
    type,
    form
  ) {

    const config =
      configs[type];

    const payload = {};


    config.fields.forEach(
      field => {

        const input =
          form.elements[
            field.name
          ];


        if (!input) {
          return;
        }


        if (
          field.kind ===
          'checkbox'
        ) {

          payload[field.name] =
            Boolean(
              input.checked
            );

          return;

        }


        if (
          field.kind ===
          'number'
        ) {

          payload[field.name] =
            numberOrNull(
              input.value
            );

          return;

        }


        if (
          field.kind ===
          'csv'
        ) {

          payload[field.name] =
            csvToArray(
              input.value
            );

          return;

        }


        payload[field.name] =
          clean(
            input.value
          );

      }
    );


    return payload;

  }


  async function saveRelated(
    form,
    button
  ) {

    const collegeId =
      form.dataset
        .pncmsCollegeId;

    const rowId =
      form.dataset
        .pncmsRowId ||
      '';

    const type =
      form.dataset
        .pncmsRelatedForm;


    if (!collegeId) {

      notify(
        'Save the college overview first.',
        true
      );

      return;

    }


    if (
      typeof form.reportValidity ===
      'function' &&
      !form.reportValidity()
    ) {

      return;

    }


    let table = '';
    let payload = {
      college_id:
        collegeId
    };


    if (
      type ===
      'course'
    ) {

      table =
        'college_courses';

      payload = {
        college_id:
          collegeId,

        course_id:
          clean(
            form.elements
              .course_id
              ?.value
          ),

        department:
          clean(
            form.elements
              .department
              ?.value
          ),

        specialization:
          clean(
            form.elements
              .specialization
              ?.value
          ),

        duration_years:
          numberOrNull(
            form.elements
              .duration_years
              ?.value
          ),

        seats:
          numberOrNull(
            form.elements
              .seats
              ?.value
          ),

        is_active:
          Boolean(
            form.elements
              .is_active
              ?.checked
          )
      };


      if (!payload.course_id) {

        notify(
          'Choose a program first.',
          true
        );

        return;

      }

    }


    else if (
      type ===
      'exam'
    ) {

      table =
        'college_entrance_exams';

      payload = {
        college_id:
          collegeId,

        exam_id:
          clean(
            form.elements
              .exam_id
              ?.value
          ),

        college_course_id:
          clean(
            form.elements
              .college_course_id
              ?.value
          ),

        is_required:
          Boolean(
            form.elements
              .is_required
              ?.checked
          ),

        notes:
          clean(
            form.elements
              .notes
              ?.value
          )
      };


      if (!payload.exam_id) {

        notify(
          'Choose an entrance exam first.',
          true
        );

        return;

      }

    }


    else if (
      type ===
      'comparison'
    ) {

      table =
        'college_comparison';


      [
        ...comparisonCampusFields,
        ...comparisonCoreFields
      ]
        .forEach(
          field => {

            const input =
              form.elements[
                field.name
              ];


            if (input) {

              payload[field.name] =
                clean(
                  input.value
                );

            }

          }
        );

    }


    else if (
      configs[type]
    ) {

      const config =
        configs[type];

      table =
        config.table;

      payload = {
        college_id:
          collegeId,
        ...readConfigPayload(
          type,
          form
        )
      };

    }


    else {

      return;

    }


    button.disabled =
      true;

    const original =
      button.textContent;

    button.textContent =
      'Saving…';


    try {

      const DB =
        await waitForDB();


      await DB.request(
        table,
        rowId
          ? `id=eq.${encodeURIComponent(rowId)}`
          : '',
        {
          method:
            rowId
              ? 'PATCH'
              : 'POST',

          headers:{
            Prefer:
              'return=representation'
          },

          body:
            payload
        }
      );


      notify(
        rowId
          ? 'Changes saved.'
          : 'Record added.'
      );


      const editorForm =
        document.querySelector(
          '[data-pncms-form]'
        );


      if (editorForm) {

        await loadCollegeData(
          editorForm
        );

      }

    }


    catch (error) {

      console.error(
        '[PeerNest College CMS] save:',
        error
      );


      notify(
        error?.message ||
        'Could not save this record.',
        true
      );


      alert(
        error?.message ||
        'Could not save this record.'
      );

    }


    finally {

      button.disabled =
        false;

      button.textContent =
        original;

    }

  }


  async function deleteRelated(
    button
  ) {

    const table =
      button.dataset
        .pncmsTable;

    const id =
      button.dataset
        .pncmsId;

    const collegeId =
      button.dataset
        .pncmsCollege;


    if (
      !table ||
      !id ||
      !collegeId
    ) {

      return;

    }


    if (
      !window.confirm(
        'Remove this college record?'
      )
    ) {

      return;

    }


    button.disabled =
      true;


    try {

      const DB =
        await waitForDB();


      await DB.request(
        table,
        `id=eq.${encodeURIComponent(id)}`,
        {
          method:'DELETE'
        }
      );


      notify(
        'Record removed.'
      );


      const editorForm =
        document.querySelector(
          '[data-pncms-form]'
        );


      if (editorForm) {

        await loadCollegeData(
          editorForm
        );

      }

    }


    catch (error) {

      console.error(
        '[PeerNest College CMS] delete:',
        error
      );


      notify(
        error?.message ||
        'Could not remove this record.',
        true
      );


      alert(
        error?.message ||
        'Could not remove this record.'
      );


      button.disabled =
        false;

    }

  }


  function enhanceEditor(
    form
  ) {

    if (
      !form ||
      form.dataset
        .pncmsDataEnhanced ===
      'true'
    ) {

      return;

    }


    form.dataset
      .pncmsDataEnhanced =
      'true';


    insertExtraOverview(
      form
    );

    insertTabsAndPanels(
      form
    );

    loadCollegeData(
      form
    );

  }


  function scan() {

    const form =
      document.querySelector(
        '[data-pncms-form]'
      );


    if (form) {

      enhanceEditor(
        form
      );

    }

  }


  const observer =
    new MutationObserver(
      scan
    );


  function boot() {

    observer.observe(
      document.documentElement,
      {
        childList:true,
        subtree:true
      }
    );


    scan();

  }


  document.addEventListener(
    'click',
    event => {

      const save =
        event.target.closest(
          '[data-pncms-data-save]'
        );


      if (save) {

        event.preventDefault();

        const form =
          save.closest(
            '[data-pncms-related-form]'
          );


        if (form) {

          saveRelated(
            form,
            save
          );

        }

        return;

      }


      const remove =
        event.target.closest(
          '[data-pncms-data-delete]'
        );


      if (remove) {

        event.preventDefault();

        deleteRelated(
          remove
        );

      }

    }
  );


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      boot,
      {
        once:true
      }
    );

  }

  else {

    boot();

  }

})();


(() => {

  'use strict';

  /* PN_MOBILE_COLLEGE_EDITOR_CONTROLLER */


  const mobile =
    window.matchMedia(
      '(max-width:760px)'
    );


  function closeMobileEditor() {

    document.body
      .classList
      .remove(
        'pncms-mobile-editor-open'
      );

  }


  function ensureBackButton(
    editor
  ) {

    if (
      editor.querySelector(
        '[data-pncms-mobile-back]'
      )
    ) {

      return;

    }


    const button =
      document.createElement(
        'button'
      );

    button.type =
      'button';

    button.className =
      'pncms-mobile-back';

    button.dataset
      .pncmsMobileBack =
      '';

    button.innerHTML =
      '← Back to colleges';


    editor.prepend(
      button
    );

  }


  function openMobileEditor() {

    if (!mobile.matches) {
      return;
    }


    window.setTimeout(
      () => {

        const editor =
          document.querySelector(
            '[data-pncms-editor]'
          );

        const form =
          editor?.querySelector(
            '[data-pncms-form]'
          );


        if (
          !editor ||
          !form
        ) {

          return;

        }


        ensureBackButton(
          editor
        );


        document.body
          .classList
          .add(
            'pncms-mobile-editor-open'
          );


        editor.scrollTop =
          0;

      },
      60
    );

  }


  document.addEventListener(
    'click',
    event => {

      if (
        event.target.closest(
          '[data-pncms-add], [data-pncms-edit]'
        )
      ) {

        openMobileEditor();

        return;

      }


      if (
        event.target.closest(
          '[data-pncms-mobile-back]'
        )
      ) {

        event.preventDefault();

        closeMobileEditor();

      }

    },
    true
  );


  window.addEventListener(
    'hashchange',
    () => {

      if (
        window.location.hash !==
        '#colleges'
      ) {

        closeMobileEditor();

      }

    }
  );


  mobile.addEventListener
    ?.(
      'change',
      event => {

        if (!event.matches) {

          closeMobileEditor();

        }

      }
    );

})();
